import { uuidv4 } from "@/utils"
import { ADD_BUTTON_CELL_HEIGHT, ADDON_ROW_ID_PREFIX, BID_STATUS_PENDING_EP, BID_STATUS_PENDING_FINANCE, LINE_GROUP_ROW_ID_PREFIX, LINE_ITEM_ROW_ID_PREFIX, ROW_CELL_HEIGHT, HOURS_PER_DAY, WORK_DAYS_PER_WEEK } from "./constants"
import { getRow , headerRow } from "./sheetUtils"
import { AddButtonCell } from "./AddButtonCell"
import { getServiceItemsByCategory } from "./serviceItemsStore"
import { BID_STATUS_DRAFT, bidCategories, bidSections } from "@/sheet/constants"
import { columnProfiles } from "@/sheet/bidColumns"
export { columnProfiles } from "@/sheet/bidColumns"

export const store = {
    bid: {}, 
    lastRequestTimestamp: null,
    localData: {
        expantions: {},
        openedDropdownCell: {
            rowId: null,
            columnId: null
        },
    },
}

const boldedColumns = [
    "name", "client_budget", "client_quoted"
]

const fieldsSelectedValueMapping = {
    is_internal: {
        true: "Internal",
        false: "External",
    },
}

const getAddButtonRow = ( type, parentGroup, parentGroupRowId, serviceItems, depth ) => {
    const processedColumnProfiles = getProcessedColumnProfiles()
    const addButtonHasChildren = false
    let addButtonColumnProfile = processedColumnProfiles.map((profile, index)=>profile.id=="name" ? {
        id: "name",
        type: "text", 
        nonEditable: true,
        alwaysVisible: true,
        renderer: () => 
        <div style={{paddingLeft: depth * 20 + 32, width: "100%"}}>
            <AddButtonCell 
                type={type}
                parent={parentGroup || store.bid}
                serviceItemOptions={serviceItems}
                onAddGroup={(groupId)=>{
                    store.localData.expantions[groupId] = true
                }}
            />
        </div>
    } : {id: "", type: "text", text: ""})
    return getRow(
        {id: uuidv4(), depth: depth}, 
        addButtonColumnProfile.map((col, index)=>index == 1 ? {...col, colspan: addButtonColumnProfile.length - 1} : col), 
        addButtonHasChildren, 
        parentGroupRowId,
        depth,
        ADD_BUTTON_CELL_HEIGHT
    )
}

export const preProcessColumnProfiles = (canViewRestrictedData, canEditExchangeRates) => {
    columnProfiles.forEach(col=>{
        if (col.formatter) {
            col.format = col.formatter(store.bid.project?.currency)
        }
        if (col.restricted && canViewRestrictedData) {
            col.restricted = false
        }
        if (col.id == "exchange_rate_buffer") {
            col.nonEditable = !canEditExchangeRates
        }
    })
}

export const getProcessedColumnProfiles = () => {
    return columnProfiles.filter(col => ((col.visible || col.alwaysVisible) && !col.restricted)).toSorted((a, b) => Boolean(a.sticky) - Boolean(b.sticky))
}

export const bidToRows = () => {
    const processedColumnProfiles = getProcessedColumnProfiles()
    let rows = []
    let stack = [...store.bid.line_items_grouped, {addButton: "section", depth: 0, parent_id: null}].reverse()
    while (stack.length > 0) {
        const currentGroup = stack.pop()
        const currentGroupHasChildren = true
        const groupRowId = `${LINE_GROUP_ROW_ID_PREFIX}-${currentGroup.id}`
        const groupParentRowId = currentGroup.parent_id && `${LINE_GROUP_ROW_ID_PREFIX}-${currentGroup.parent_id}`
        const lineItemHasChildren = false
        const addonHasChildren = false
        const currentGroupDepth = currentGroup.depth || 0
        const nextGroupDepth = currentGroupDepth + 1

        if (currentGroup.addButton) {
            const parentGroup = store.bid.line_groups.find(group=>group.id==currentGroup.parent_id)
            if (parentGroup) {
                parentGroup.addons.forEach(addon=>{
                    const addonParentId = addon.line_group_id && `${LINE_GROUP_ROW_ID_PREFIX}-${addon.line_group_id}`
                    const addonRowId = `${ADDON_ROW_ID_PREFIX}-${addon.id}`
                    rows.push(getRow(
                        {...addon, depth: nextGroupDepth, id: addonRowId}, 
                        processedColumnProfiles, 
                        addonHasChildren, 
                        addonParentId, 
                        currentGroupDepth,
                        ROW_CELL_HEIGHT,
                        null,
                    ))
                })
            }
    
            rows.push(getAddButtonRow(
                currentGroup.addButton,
                parentGroup,
                groupParentRowId,
                currentGroup.addButton == "section" ? bidSections : bidCategories,
                currentGroupDepth
            ))
            continue
        }

        rows.push(getRow(
            {...currentGroup, id: groupRowId}, 
            processedColumnProfiles.map((col, index)=>index == 1 ? {...col, colspan: processedColumnProfiles.length - 4} : col),
            currentGroupHasChildren, 
            groupParentRowId, 
            currentGroup.depth,
            ROW_CELL_HEIGHT,
            store.localData.expantions[groupRowId],
            store.localData.openedDropdownCell,
            false,
            fieldsSelectedValueMapping,
            ["is_internal", "rate_type", "markup"]
        ))

        currentGroup.line_items.forEach(line=>{
            const lineItemParentId = line.line_group_id && `${LINE_GROUP_ROW_ID_PREFIX}-${line.line_group_id}`
            const lineItemRowId = `${LINE_ITEM_ROW_ID_PREFIX}-${line.id}`
            rows.push(getRow(
                {...line, depth: nextGroupDepth, id: lineItemRowId}, 
                processedColumnProfiles, 
                lineItemHasChildren, 
                lineItemParentId, 
                nextGroupDepth,
                ROW_CELL_HEIGHT,
                null,
                store.localData.openedDropdownCell,
                false,
                fieldsSelectedValueMapping
            ))
        })

        if (currentGroup.line_items.length > 0 || currentGroup.depth >= 1) {
            currentGroup.addons.forEach(addon=>{
                const addonParentId = addon.line_group_id && `${LINE_GROUP_ROW_ID_PREFIX}-${addon.line_group_id}`
                const addonRowId = `${ADDON_ROW_ID_PREFIX}-${addon.id}`
                rows.push(getRow(
                    {...addon, depth: nextGroupDepth, id: addonRowId}, 
                    processedColumnProfiles, 
                    addonHasChildren, 
                    addonParentId, 
                    nextGroupDepth,
                    ROW_CELL_HEIGHT,
                    null,
                ))
            })
            const serviceItemNames = getServiceItemsByCategory(currentGroup.name).map(item=>item.name)
            rows.push(getAddButtonRow("line", currentGroup, groupRowId, serviceItemNames, nextGroupDepth))
        }

        let childrenWithDepthInfo = [...currentGroup.children.map(group=>{return {...group, depth: nextGroupDepth}})]
        if (!currentGroup.parent_id) {
            childrenWithDepthInfo = [...childrenWithDepthInfo, {addButton: "category", depth: nextGroupDepth, parent_id: currentGroup.id}].reverse()
        }
        stack = stack.concat(childrenWithDepthInfo)
    }
    const header = headerRow(processedColumnProfiles)
    const allRows = [header, ...rows]

    return allRows
}

const constructLineItemsGrouped = () => {
    const lineItems = store.bid.line_items
    const lineGroups = store.bid.line_groups
    const addons = store.bid.addons

    const sections = lineGroups.filter(group=>!group.parent_id).sort((a, b)=>a.sort_order - b.sort_order)
    const categories = lineGroups.filter(group=>group.parent_id)

    const sectionCategories = Object.fromEntries(sections.map(section=>[section.id, []]))
    const categoryLineItems = Object.fromEntries(categories.map(category=>[category.id, []]))
    const groupAddons = Object.fromEntries(lineGroups.map(group=>[group.id, []]))

    categories.forEach(category=>{
        sectionCategories[category.parent_id].push(category)
        category.children = []
        category.line_items = categoryLineItems[category.id].sort((a, b)=>a.sort_order - b.sort_order)
        category.addons = groupAddons[category.id]
    })

    sections.forEach(section=>{
        section.line_items = []
        section.children = sectionCategories[section.id].sort((a, b)=>a.sort_order - b.sort_order)
        section.addons = groupAddons[section.id]
    })

    lineItems.forEach(lineItem=>{
        categoryLineItems[lineItem.line_group_id].push(lineItem)
    })

    addons.forEach(addon=>{
        if (addon.line_group_id){
            groupAddons[addon.line_group_id].push(addon)
        }
    })

    return sections
}

export const storeBid = (bid) => {
    store.bid = bid
    store.bid.project_rd_bid_ids = []
    store.bid.line_items_grouped = constructLineItemsGrouped()
    storeInsights(bid, bid.status == BID_STATUS_DRAFT || bid.status == BID_STATUS_PENDING_EP || bid.status == BID_STATUS_PENDING_FINANCE)
    store.bid.top_sheet_sender_ids = bid.top_sheet_senders.map((sender)=>{return sender.id})
}

export const getLineItemsHash = () => {
    if (!store.bid.line_items) {
        return {}
    }
    return Object.fromEntries(store.bid.line_items.map(line => [line.id, line]))
}

export const getLineGroupsHash = () => {
    if (!store.bid.line_groups) {
        return {}
    }
    return Object.fromEntries(store.bid.line_groups.map(group => [group.id, group]))
}

export const getAddonsHash = () => {
    if (!store.bid.addons) {
        return {}
    }
    return Object.fromEntries(store.bid.addons.map(addon => [addon.id, addon]))
}

export const getQuotedThreshold = (external_budget, external_percentage, internal_budget, internal_percentage) => {
    return (external_budget / (1 - external_percentage)) + (internal_budget / (1 - internal_percentage))
}

export const storeInsights = (insights, includeBidinProject) => {
    const {
        external_quoted,
        internal_quoted,
        addons_quoted,
        total_quoted,
        external_budget,
        internal_budget,
        internal_budget_with_internal_rate,
        gross_profit,
        overhead,
        net_profit,
        project,
    } = insights

    store.bid.insights = {
        external_quoted,
        internal_quoted,
        addons_quoted,
        total_quoted,
        external_budget,
        internal_budget,
        internal_budget_with_internal_rate,
        gross_profit,
        overhead,
        net_profit,
    }

    if (project) {
        store.bid.insights = {
            ...store.bid.insights,
            project_external_quoted: project.external_quoted,
            project_internal_quoted: project.internal_quoted,
            project_addons_quoted: project.addons_quoted,
            project_total_quoted: project.total_quoted,
            project_external_budget: project.external_budget,
            project_internal_budget: project.internal_budget,
            project_internal_budget_with_internal_rate: project.internal_budget_with_internal_rate,
            project_gross_profit: project.gross_profit,
            project_overhead: project.overhead,
            project_net_profit: project.net_profit,
        }
    }

    if (includeBidinProject) {
        store.bid.insights.project_external_quoted += external_quoted
        store.bid.insights.project_internal_quoted += internal_quoted
        store.bid.insights.project_addons_quoted += addons_quoted
        store.bid.insights.project_total_quoted += total_quoted
        store.bid.insights.project_external_budget += external_budget
        store.bid.insights.project_internal_budget += internal_budget
        store.bid.insights.project_internal_budget_with_internal_rate += internal_budget_with_internal_rate
        store.bid.insights.project_gross_profit += gross_profit
        store.bid.insights.project_overhead += overhead
        store.bid.insights.project_net_profit += net_profit
    }

    store.bid.insights.target_quoted = getQuotedThreshold(
        store.bid.insights.project_external_budget, 0.15, 
        store.bid.insights.project_internal_budget_with_internal_rate, 0.5
    )
    
    store.bid.insights.minimum_quoted = getQuotedThreshold(
        store.bid.insights.project_external_budget, 0.1, 
        store.bid.insights.project_internal_budget_with_internal_rate, 0.5
    )
}

// TEMP for legacy bid integration
export const storeBills = (bills) => {
    store.bid.bills = bills
}

export const getLineItem = (lineId) => {
    return getLineItemsHash()[lineId]
}

export const getLineGroup = (lineGroupId) => {
    return getLineGroupsHash()[lineGroupId]
}

export const getAddon = (addonId) => {
    return getAddonsHash()[addonId]
}

export const getOfficeCurrency = () => {
    return store.bid.project?.legal_entity.country.currency || "CAD"
}

export const getClientCurrency = () => {
    return store.bid.project?.currency || "CAD"
}

export const getReadOnly = () => {
    return store.bid.status != BID_STATUS_DRAFT
}

export const getRootAddons = () => {
    if (!store.bid.addons) {
        return []
    }
    return store.bid.addons.filter(addon=>!addon.line_group_id)
}

export const fitColumnWidthsToContainer = (container) => {
    const processedColumnProfiles = getProcessedColumnProfiles()
    const columnsToFit = processedColumnProfiles.filter(profile=>!profile.defaultWidth)
    const allVisibleFixedColumn = processedColumnProfiles.filter(profile=>profile.defaultWidth)
    const totalFixedWidth = allVisibleFixedColumn.reduce((acc, col) => acc + col.width,0)
    const newWidth = (container.clientWidth - totalFixedWidth) / columnsToFit.length
    const width = Math.max(newWidth, 120)
    columnsToFit.forEach(profile=>{
        profile.width = width
    })
}

export const getStyledRows = (rowsData, themeMode) => {
    const readOnly = getReadOnly()
    const rows = [...rowsData]
    const styles = themeMode == "light" ? [
        {
            section: {color: "#225982", background: "#94D1FF"},
            category: {color: "#225982", background: "#C8E7FE"},
            line: {color: "#2C8FDA", background: "#EEF5FF"},
            disabled: {color: "#9E9E9E", background: "#F2F2F2"},
        },
        {
            section: {color: "#724707", background: "#FFDAA2"},
            category: {color: "#955D09", background: "#FCEACE"},
            line: {color: "#CB7F0E", background: "#FAF7F2"},
            disabled: {color: "#9E9E9E", background: "#F2F2F2"},
        },
        {
            section: {color: "#055832", background: "#95E7C1"},
            category: {color: "#055832", background: "#BBEFD7"},
            line: {color: "#148451", background: "#E8FCF3"},
            disabled: {color: "#9E9E9E", background: "#F2F2F2"},
        },
    ] : [
        {
            section: {color: "#D5EDFF", background: "#0082E4"},
            category: {color: "#D5EDFF", background: "#015A9D"},
            line: {color: "#D5EDFF", background: "#002E50"},
            disabled: {color: "#9E9E9E", background: "#F2F2F222"},
        },
        {
            section: {color: "#FFECD0", background: "#D08D28"},
            category: {color: "#FFECD0", background: "#9F6A1C"},
            line: {color: "#FFECD0", background: "#533911"},
            disabled: {color: "#9E9E9E", background: "#F2F2F222"},
        },
        {
            section: {color: "#DAEEE5", background: "#21A468"},
            category: {color: "#DAEEE5", background: "#1A754C"},
            line: {color: "#DAEEE5", background: "#0C4129"},
            disabled: {color: "#9E9E9E", background: "#F2F2F222"},
        },
    ]
    // Set hiararchy colors
    const processedColumnProfiles = getProcessedColumnProfiles()

    rows.forEach(row=>{
        const stylesStack = [...styles]
        const isCategory = row.rowId.split("-")[0] == LINE_GROUP_ROW_ID_PREFIX && row.parentId != null
        const isSection = row.rowId.split("-")[0] == LINE_GROUP_ROW_ID_PREFIX && row.parentId == null

        for (var i = row.cells.length - 1; i > 0; i -- ) {
            const cell = row.cells[i]
            if (!cell.style) {
                cell.style = {}
            }
            if (processedColumnProfiles[i].sticky) {
                const style = stylesStack.pop()
                const cellStyle = readOnly ? style.disabled : (isSection ? style.section : isCategory ? style.category : style.line)
                cell.style.color = cellStyle.color
                cell.style.background = cellStyle.background
            } else {
                if (readOnly) {
                    cell.style.color = "#9E9E9E"
                    cell.nonEditable = true
                }
            }
        }
    })

    // Set cell border color

    if (themeMode == "dark") {
        rows.forEach(row=>{
            row.cells.forEach(cell=>{
                cell.style = {...cell.style, border: {
                    top: {color: "#464646"},
                    bottom: {color: "#464646"},
                    left: {color: "#464646"},
                    right: {color: "#464646"},
                }}
                cell.style.boxShadow = "none"
            })
        })
    }

    // Set outer table boarder

    const firstRowCells = rows[0].cells
    const lastRowCells = rows[rows.length-1].cells
    const firstColumnCells = rows.map(row=>row.cells[0])
    const lastColumnCells = rows.map(row=>row.cells[row.cells.length-1])
    firstRowCells.forEach(cell=>{
        if (!cell.style) {
            cell.style = {}
        }
        cell.style = {...cell.style, border: {...cell.style.border, top: {color: "transparent"}}}
    })
    firstColumnCells.forEach(cell=>{
        cell.style = {...cell.style, border: {...cell.style.border, left: {color: "transparent"}}}
    })
    lastRowCells.forEach(cell=>{
        cell.style = {...cell.style, border: {...cell.style.border, bottom: {color: "transparent"}}}
    })
    lastColumnCells.forEach(cell=>{
        cell.style = {...cell.style, border: {...cell.style.border, right: {color: "transparent"}}}
    })

    // Set row styles

    rows.forEach(row=>{
        const isSection = row.rowId.split("-")[0] == LINE_GROUP_ROW_ID_PREFIX && row.parentId == null
        if (isSection) {
            row.cells.forEach(cell=>{
                if (boldedColumns.includes(cell.columnId)) {
                    cell.style.fontWeight = 800
                }
            })
        }
    })

    return rows

}

export function getLineItemRate(existingRate, lineItem, rateType) {
    if (!lineItem || !lineItem.vendor_rate) {
        return existingRate
    }

    switch(rateType) {
        case "Hours":
            return Math.round(lineItem.vendor_rate / HOURS_PER_DAY * 100) / 100
        case "Days":
        case "Percentage":
            return lineItem.vendor_rate
        case "Weeks":
            return lineItem.vendor_rate * WORK_DAYS_PER_WEEK
        case "Flat rate":
        default:
            return existingRate
    }
}

export function getIsProjctPepsi(){
    if (!store.bid.project || !store.bid.project.client) {
        return false
    }
    return store.bid.project.client.name.includes("Pepsi")
}
